import { useQuery, type QueryClient } from "@tanstack/react-query";
import { api } from "../api";
import type { ExtractantFactors } from "../lib/extractantFactors";
import { queryClient } from "../lib/queryClient";
import { useOrgStore } from "../stores/orgStore";

// Kept off hooks/queries.ts so a test that mocks that module still loads the lens.
export const extractantFactorsKey = ["extractant-factors"] as const;

// Cached against compute's `version` so a reload converts immediately instead of showing M3
// numbers until the fetch lands. The cache is a cache: the query always refetches, and a retune
// takes effect on the next load. Until factors are available nothing is converted.

const KEY = "sm.extractantFactors.v1";

function readCached(): ExtractantFactors | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const f = JSON.parse(raw) as ExtractantFactors;
    return f && typeof f.version === "string" && f.from_m3 ? f : null;
  } catch {
    return null;
  }
}

function remember(next: ExtractantFactors, prev: ExtractantFactors | null) {
  if (next.version === prev?.version) return;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private mode — live for this session */
  }
}

// Seed the query cache once, before React mounts, so the first paint can convert.
function seedFromCache() {
  if (queryClient.getQueryData(extractantFactorsKey) !== undefined) return;
  const cached = readCached();
  if (cached) queryClient.setQueryData(extractantFactorsKey, cached);
}
seedFromCache();

async function fetchFactors(client: QueryClient): Promise<ExtractantFactors> {
  const prev =
    client.getQueryData<ExtractantFactors>(extractantFactorsKey) ?? null;
  try {
    const f = await api.extractantFactors();
    if (!f?.from_m3) {
      // A response with no usable payload is indistinguishable from a fetch that never ran.
      console.warn(
        "[extractant factors] the response carried no `from_m3` — nothing will convert",
        f,
      );
      if (prev) return prev;
      throw new Error("extractant factors missing from_m3");
    }
    remember(f, prev);
    return f;
  } catch (e) {
    if (
      e instanceof Error &&
      e.message === "extractant factors missing from_m3"
    )
      throw e;
    // Keep the cached set. A fetch failure is not a reason to show different numbers.
    console.warn(
      "[extractant factors] could not refresh — using the cached set",
      e,
    );
    if (prev) return prev;
    throw e;
  }
}

export function useExtractantFactorsQuery(enabled = true) {
  const orgId = useOrgStore((s) => s.activeOrgId);
  return useQuery({
    queryKey: extractantFactorsKey,
    queryFn: ({ client }) => fetchFactors(client),
    enabled: enabled && !!orgId,
    staleTime: 0,
    refetchOnMount: "always",
    retry: false,
  });
}

// Current factors for the pure formatters, which are not components. An org switch clears the
// query cache; the local copy keeps the lens steady until the refetch lands.
export function extractantFactors(): ExtractantFactors | null {
  const data = queryClient.getQueryData<ExtractantFactors | null>(
    extractantFactorsKey,
  );
  if (data !== undefined) return data;
  return readCached();
}

// Tests plant a factor set the way the old store's setState did.
export function setExtractantFactors(factors: ExtractantFactors | null): void {
  queryClient.setQueryData(extractantFactorsKey, factors);
}

export const useExtractantFactors = {
  setState(next: { factors: ExtractantFactors | null }) {
    setExtractantFactors(next.factors);
  },
};
